/*
 * Копия `sc-frontend-atyrau/src/lib/agentMarkdown.ts` — правки разбора нести в оба.
 *
 * Разбор ответа ИИ-агента в узлы для шаблона.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ НУЖНО. Агент отвечает разметкой Markdown: `**ЖКХ**`,
 * списки через `- `, иногда заголовки. Пока панель печатала ответ одной
 * строкой, человек видел на экране сами звёздочки — «- **Объёмы и динамика**
 * — сколько обращений». Это не косметика: выделение жирным у агента несёт
 * смысл (в нём идут сами числа и названия сфер), и в виде звёздочек оно
 * пропадает, а текст становится грязнее, чем был бы без разметки вовсе.
 *
 * ПОЧЕМУ НЕ БИБЛИОТЕКА И НЕ `dangerouslySetInnerHTML`. Полный Markdown отдал
 * бы в разметку HTML из чужого сервиса, а текст ответа собирает языковая
 * модель по содержимому обращений граждан, то есть по внешнему вводу. Здесь
 * разбор даёт ДАННЫЕ, а компонент рисует их обычными узлами React — вставить
 * тег в DOM через этот путь невозможно в принципе.
 *
 * РАЗБОР ОБЯЗАН ПЕРЕЖИВАТЬ ОБОРВАННУЮ СТРОКУ. Ответ печатается посимвольно, и
 * парсер вызывается на каждом куске: `**Объ` придёт раньше, чем закроется
 * пара звёздочек. Незакрытый `**` считается открытым выделением, а не текстом,
 * иначе звёздочки на долю секунды мигали бы в конце каждой строки.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Таблиц, ссылок и кода блоками. Агент их пока не присылает; и
 * если начнёт, лучше увидеть исходный текст, чем молча потерять содержимое.
 */

export interface RichSpan {
  text: string
  bold: boolean
  /** `код в обратных кавычках` — у агента так приходят имена полей и SQL. */
  code: boolean
}

export interface RichLine {
  /** Маркер списка: «•» для `- `, сам номер для `1. `. Пусто — обычная строка. */
  marker: string
  /** Заголовок (`## …`): строка целиком идёт выделенной. */
  heading: boolean
  /** Пустая строка-разделитель между абзацами. */
  blank: boolean
  spans: RichSpan[]
}

/** Разбивает строку по парным ограничителям; непарный открывает выделение до конца. */
function splitBy(spans: RichSpan[], mark: string, flag: 'bold' | 'code'): RichSpan[] {
  const out: RichSpan[] = []
  for (const span of spans) {
    // Внутри уже выделенного куска второй раз не разбираем: `**` внутри
    // кода — это звёздочки, а не выделение.
    if (span.bold || span.code) {
      out.push(span)
      continue
    }
    let rest = span.text
    let inside = false
    while (rest.length) {
      const at = rest.indexOf(mark)
      if (at === -1) {
        out.push({ ...span, text: rest, [flag]: inside } as RichSpan)
        break
      }
      if (at > 0) out.push({ ...span, text: rest.slice(0, at), [flag]: inside } as RichSpan)
      rest = rest.slice(at + mark.length)
      inside = !inside
    }
  }
  return out.filter((s) => s.text.length > 0)
}

export function parseAgentText(text: string): RichLine[] {
  return text.split('\n').map((raw) => {
    const line = raw.trimEnd()
    if (!line.trim()) return { marker: '', heading: false, blank: true, spans: [] }

    let body = line
    let marker = ''
    let heading = false

    const head = /^#{1,6}\s+/.exec(body)
    if (head) {
      body = body.slice(head[0].length)
      heading = true
    }

    const bullet = /^\s*[-*•]\s+/.exec(body)
    const numbered = /^\s*(\d{1,2})[.)]\s+/.exec(body)
    if (bullet) {
      marker = '•'
      body = body.slice(bullet[0].length)
    } else if (numbered) {
      marker = `${numbered[1]}.`
      body = body.slice(numbered[0].length)
    }

    let spans: RichSpan[] = [{ text: body, bold: heading, code: false }]
    spans = splitBy(spans, '**', 'bold')
    spans = splitBy(spans, '`', 'code')
    // Заголовок выделен целиком — разбор мог сбросить флаг на кусках.
    if (heading) spans = spans.map((s) => ({ ...s, bold: true }))

    return { marker, heading, blank: false, spans }
  })
}
